import React from 'react';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import './onboarding.scss'
interface FirstStepProps {
  onNext: (step: number, type: 'sms' | 'mms') => void;
}

const icons = {
  sms: '/media/icons/duotune/navigationicon/messages.svg',
  mms: '/media/icons/duotune/navigationicon/bulkmsg.svg',
  email: '/media/icons/duotune/communication/com002.svg',
  smart: '/media/icons/duotune/navigationicon/data.svg',
}
type IconType = keyof typeof icons;

interface CardItemProps {
  onNext: (step: number, type: 'sms' | 'mms') => void;
  title: string;
  subTitle: string;
  icon: IconType;
  items: string[];
  sale?: boolean
  badge?: string
}

const colors = {
  sms: {
    background: '#ecfef4',
    color: '#74ca8f'
  },
  mms: {
    background: '#eef6ff',
    color: '#3e97ff'
  },
  email: {
    background: 'rgba(245, 222, 179, 0.2)',
    color: '#f5deb3'
  },
  smart: {
    background: '#f5f8fa',
    color: '#a1a5b7'
  }
}

const CardItem: React.FC<CardItemProps> = ({ onNext, title, subTitle, items, sale, badge, icon }) => {
  return (
    <Card className="h-100 border-0 p-2 position-relative">
      {sale && (
        <div className="position-absolute top-0 start-75 translate-middle badge rounded-pill bg-danger py-2 px-3 sale-badge">
          Sale Now On!
        </div>
      )}

      <Card.Body className='d-flex flex-column gap-4'>
        <div className='d-flex gap-2 align-items-center'>
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle p-3"
            style={{ background: colors[icon].background }}
          >
            <KTSVG
              path={icons[icon]}
              className="svg-icon-2x"
              style={{ color: colors[icon].color }}
            />
          </div>
          <div className='fs-3 fw-bolder'>{title}</div>
        </div>
        {badge && (
          <div className='d-flex gap-2 px-2 py-2 rounded-2 fw-bolder' style={{ background: "oklch(.971 .013 17.38)", border: '1px solid #9f2d00' }}>
            <span role="img" aria-label="warning">🚀</span> <span style={{ color: "#9f2d00" }}>{badge}</span>
          </div>
        )}
        <div className='fs-6 text-gray-600'>{subTitle}</div>
        <div className="text-gray-600">
          {items && items.map((item) => (
            <div className="border-0 bg-transparent py-1 d-flex gap-2" key={item}>
              <span className="text-success me-2">✓</span>
              <span className='fs-6'>{item}</span>
            </div>
          ))}
        </div>
      </Card.Body>
      <div className="p-3">
        <Button disabled={title === 'Smart Campaign' || title === 'Email'} variant="primary" className="w-100" onClick={() => onNext(2, title as 'sms' | 'mms')}>Send now</Button>
      </div>
    </Card>
  )
}

const FirstStep: React.FC<FirstStepProps> = ({ onNext }) => {
  return (
    <div className="position-relative pt-10" style={{ marginTop: "-30px" }}>
      <div
        className="position-absolute top-0 start-50 translate-middle-x"
        style={{
          width: "calc(100vw - 265px)",
          height: "32.5%",
          background: "linear-gradient(to right, oklch(.977 .014 308.299), oklch(.97 .014 254.604), oklch(.977 .014 308.299))",
          zIndex: 0,
        }}
      ></div>
      <Card
        className='rounded-4 p-8 h-100 px-8'
        style={{ 
          backgroundImage: `url(${toAbsoluteUrl('/media/onboarding/bg-header.png')})`,
          backgroundPosition: "right center",
          backgroundRepeat: 'no-repeat',
          backgroundSize: "50% 100%",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <div>
              <span className="badge bg-warning text-white mb-2 rounded-4 px-4 py-2">
                <span className='me-2' role="img" aria-label="rocket">🚀</span> <span style={{ color: "oklch(.476 .114 61.907)" }}>Quick start</span>
              </span>
              <div className="mb-1 fs-1 fw-bolder">Your 2-min away from sending your first campaign</div>
              <p className="fs-5 text-gray-600">Maximise your campaign and speak with a specialist</p>
            </div>
            <div>
              <a
                href="https://www.cellcast.com/au/book-a-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-link w-auto"
              >
                Book appointment
              </a>
            </div>
          </div>
          <div className='h-100 d-flex flex-column align-items-center'>
            <img alt='Logo' src={toAbsoluteUrl('/media/onboarding/hero-header.png')} className='h-150px' />
          </div>
        </div>
      </Card>

      {/* Campaign Options Section */}
      <Row className="mt-10 g-4">
        {/* SMS Card */}
        <Col md={3}>
          <CardItem
            onNext={onNext}
            title="SMS"
            subTitle='Send the most reliable text messages, reach your customers instantly.'
            icon='sms'
            items={[
              'Tier one provider',
              'Ultra-fast & reliable message',
              'Local support team',
              'Simple inbox to manage replies',
              'Send with business names',
              'Two-way messaging',
              'A/B testing'
            ]}
          />
        </Col>

        {/* MMS Card */}
        <Col md={3}>
          <CardItem
            onNext={onNext}
            title="MMS"
            sale={true}
            badge='Major Upgrade! - 4x files sizes (1.5MB)'
            subTitle='Send media content, including images, videos, and documents with multimedia messaging.'
            icon='mms'
            items={[
              'Rich media messaging',
              'Telstra Partner',
              'Two-way messaging',
              'Ultra-fast & reliable',
              '1500 characters per message',
              'Powerful automations'
            ]}
          />
        </Col>

        {/* Email Card */}
        <Col md={3}>
          <CardItem
            onNext={onNext}
            title="Email"
            badge='Coming soon!'
            subTitle='Create and send professional email campaigns with advanced tracking and analytics.'
            icon='email'
            items={[
              'Easy-to-use builder HTML + Plain text',
              'Personalisation tools',
              'Optimised for deliverability',
            ]}
          />
        </Col>

        {/* Smart Campaign Card */}
        <Col md={3}>
          <CardItem
            onNext={onNext}
            title="Smart Campaign"
            badge='Coming soon!'
            subTitle='Automated multi-channel campaigns with intelligent targeting and optimisation.'
            icon='smart'
            items={[
              'Al-powered optimisation',
              'Multi-channel sending',
              'Reduce cost of every campaign',
              'Real-time performance'
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default FirstStep;